/**
 * HighlightRenderer Component
 *
 * Applies highlights to rendered content using CSS Custom Highlight API.
 * Works as a wrapper component that doesn't change the rendering of its children.
 */

import React, { useEffect, useRef } from 'react';

/**
 * Check if browser supports CSS Custom Highlight API
 */
const supportsHighlightAPI = () => {
  return typeof CSS !== 'undefined' && CSS.highlights !== undefined;
};

/**
 * Find text node and offset for a character position
 * @param {HTMLElement} container - Container element
 * @param {number} targetOffset - Target character offset
 * @returns {Object|null} Object with node and offset, or null
 */
const findTextNodeAtOffset = (container, targetOffset) => {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let currentOffset = 0;
  let node;

  while ((node = walker.nextNode())) {
    const nodeLength = node.textContent.length;

    if (currentOffset + nodeLength >= targetOffset) {
      return {
        node,
        offset: targetOffset - currentOffset
      };
    }

    currentOffset += nodeLength;
  }

  return null;
};

/**
 * Apply highlights using CSS Custom Highlight API
 * @param {Array} highlights - Array of highlight objects
 * @param {HTMLElement} container - Container element
 * @param {string} renderedText - Rendered text from DOM
 */
const applyHighlightsWithAPI = (highlights, container, renderedText) => {
  if (!supportsHighlightAPI() || !container || !renderedText) return;

  // Clear existing highlights
  CSS.highlights.clear();

  highlights.forEach((highlight) => {
    try {
      // Get TextPositionSelector from annotation
      const textPosSelector = highlight.annotation?.target?.selector?.find(
        (s) => s.type === 'TextPositionSelector'
      );

      if (!textPosSelector) return;

      const { start, end } = textPosSelector;

      // Find the actual text nodes in the rendered DOM
      const startNodeInfo = findTextNodeAtOffset(container, start);
      const endNodeInfo = findTextNodeAtOffset(container, end);

      if (!startNodeInfo || !endNodeInfo) {
        console.warn(`Could not find text nodes for highlight ${highlight.id}`);
        return;
      }

      // Create a Range for the highlight
      const range = document.createRange();
      range.setStart(startNodeInfo.node, startNodeInfo.offset);
      range.setEnd(endNodeInfo.node, endNodeInfo.offset);

      // Create and register the highlight using the CSS Highlight API
      // eslint-disable-next-line no-undef
      const highlightObj = new Highlight(range);
      CSS.highlights.set(`highlight-${highlight.id}`, highlightObj);
    } catch (error) {
      console.warn(`Failed to apply highlight ${highlight.id}:`, error);
    }
  });
};

/**
 * HighlightRenderer Component
 * Wraps content and applies highlights on top using CSS
 */
const HighlightRenderer = ({ highlights = [], onHighlightClick, children }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (supportsHighlightAPI() && containerRef.current && highlights.length > 0) {
      // Extract text from the rendered DOM itself
      const renderedText = containerRef.current.textContent;
      applyHighlightsWithAPI(highlights, containerRef.current, renderedText);
    }

    // Cleanup
    return () => {
      if (supportsHighlightAPI()) {
        CSS.highlights.clear();
      }
    };
  }, [highlights, children]);

  // Handle highlight clicks (would need to detect which highlight was clicked)
  const handleClick = () => {
    if (!onHighlightClick) return;

    // This is complex - we'd need to determine which highlight was clicked
    // Future enhancement: detect which specific highlight was clicked
  };

  return (
    <div
      ref={containerRef}
      className="highlight-container"
      onClick={handleClick}
    >
      {children}
    </div>
  );
};

export default HighlightRenderer;
